import domainDictionary from "./domainDictionary";
import { NO_FORMATS_REASON } from "./downloadContracts";

// Источник скачивания для dimension2. 'direct' — значение по умолчанию, когда
// вызывающий ничего не передал.
export const ANALYTICS_SOURCE = {
    PLAYLIST: 'playlist',
    DIRECT: 'direct',
};

// Тот же ресурс, что подключается в views/components/analytics.blade.php.
export const CONVERTER_GA_RESOURCE = 'G-XEVP9YWW3M';

/**
 * Объект ошибки, который разбирает showError(). Контракт общий с
 * tools/downloadContracts.js: buildConvertErrorResult() строит именно эту форму,
 * поэтому при изменении набора полей здесь правится и он.
 *
 * @typedef {Object} AnalyticsErrorResult
 * @property {boolean} [timeout] - запрос не уложился в таймаут
 * @property {boolean} [queue_timeout] - не дождались слота в пуле запросов
 *                              (api/convert.js, QUEUE_WAIT_TIMEOUT)
 * @property {boolean} [signing_unavailable] - подпись недоступна, запрос не отправлен
 *                              (tools/signedRequestClient.js, fail-closed)
 * @property {number} [status] - HTTP-статус ответа
 * @property {Object} [data] - тело ответа; всегда объект, читается как data.error_code
 *                              с фолбэком на data.error для 401
 * @property {string|null} [supported_resource] - null означает «ресурс не поддерживается»
 * @property {boolean} [success]
 * @property {number} [code]
 * @property {boolean} [blacklist]
 * @property {'empty'|'multiple'|'blocked'|'no_converter'|'malformed'} [no_formats] - скачать нечего:
 *   empty — ответ без пригодных форматов, multiple — по ссылке несколько видео,
 *   blocked — ссылка не прошла проверку схемы, no_converter — клиентский конвертер недоступен,
 *   malformed — ответ пришёл в форме, которую мы не умеем читать
 * @property {string} [srv] - имя сервера, уходит в dimension1 для blacklist
 */

/**
 * TEA-6094: события воронки скачивания помечаются источником через 'dimension2'.
 *
 * Значения ('playlist' | 'direct'): 'playlist' — карточка в выдаче плейлиста (MainResults.vue),
 * 'direct' — одиночное скачивание с главной формы (значение по умолчанию, когда
 * вызывающий не передал source). Нужно, чтобы success rate считался по этим
 * сценариям раздельно. Метка приходит в PlaylistCard.vue снаружи через prop
 * analytics-source, а не захардкожена в компоненте: источников может стать больше.
 *
 * Помечены: searchDownload, downloadClick, showResults, showError.
 *
 * ВАЖНО: 'dimension2' — это имя custom dimension в настройках GA4-свойства,
 * а не индекс. При заведении отчётов его нужно сопоставить с параметром события
 * в разделе Custom definitions, иначе значение не попадёт в отчёты.
 *
 * Канонические сигнатуры событий шины, через которые source сюда приходит,
 * описаны в app.js рядом с обработчиками $on.
 */
export default {
    linkInsert(link) {
        let host = getHost(link);
        gtag('event', 'link-insert', {
            'send_to': 'main',
            'event_category': 'main-page',
            'event_label': host
        });
    },
    linkSubmit(link) {
        let host = getHost(link);
        gtag('event', 'link-submit', {
            'send_to': 'main',
            'event_category': 'main-page',
            'event_label': host
        });
    },
    searchDownload(url, source) {
        gtag('event', 'search-download', {
            'send_to': 'main',
            'event_category': 'vidacha',
            'event_label': true,
            'dimension2': source || ANALYTICS_SOURCE.DIRECT
        });
    },
    downloadClick(link, format, source) {
        let host = getHost(link);
        gtag('event', 'download-click', {
            'send_to': 'main',
            'event_category': 'vidacha',
            'event_label': host,
            'dimension1': format,
            'dimension2': source || ANALYTICS_SOURCE.DIRECT
        });
    },
    showResults(link, source) {
        let host = getHost(link);
        gtag('event', 'show', {
            'send_to': 'main',
            'event_category': 'vidacha',
            'event_label': host,
            'dimension2': source || ANALYTICS_SOURCE.DIRECT
        });
    },
    // TEA-6094: source ('playlist' | 'direct') прокидывается в события успеха/ошибки,
    // чтобы в аналитике можно было считать success rate отдельно для видео из плейлиста
    /** @param e {AnalyticsErrorResult|null} */
    showError(e, source) {
        // Ветки только выбирают событие и метку; dimension2 подставляется один раз ниже,
        // иначе в новой ветке его легко забыть.
        const event = pickErrorEvent(e);

        gtag('event', event.name, {
            'send_to': 'main',
            'event_category': event.category,
            'event_label': event.label,
            ...(event.dimension1 === undefined ? {} : { 'dimension1': event.dimension1 }),
            'dimension2': source || ANALYTICS_SOURCE.DIRECT
        });
    },
    // клик по конвертируемому формату: отдельный GA-ресурс, см. CONVERTER_GA_RESOURCE
    converterLinkClick() {
        if (!window.gtag) {
            return;
        }
        gtag('event', 'converter_link_click', { send_to: CONVERTER_GA_RESOURCE });
    },
    sendWebVitalsToGA({ name, delta, id }) {
        gtag('event', name, {
            value: delta,
            value_category: getValueCategory(name, delta),
            metric_id: id,
        });

        function getValueCategory(name, delta) {
            switch (name) {
                case 'LCP':
                    return delta <= 2500 ? 'good' : delta <= 4000 ? 'medium' : 'bad';
                case 'FID':
                    return delta <= 100 ? 'good' : delta <= 300 ? 'medium' : 'bad';
                case 'CLS':
                    return delta <= 0.1 ? 'good' : delta <= 0.25 ? 'medium' : 'bad';
                case 'FCP':
                    return delta <= 1800 ? 'good' : delta <= 3000 ? 'medium' : 'bad';
                case 'INP':
                    return delta <= 200 ? 'good' : delta <= 500 ? 'medium' : 'bad';
                case 'TTFB':
                    return delta <= 800 ? 'good' : delta <= 1800 ? 'medium' : 'bad';
                default:
                    return 'unknown metric';
            }
        };
    },
}

// причина из buildNoFormatsResult -> метка события. Ключи берём из общей константы,
// а не литералами: раньше значения жили здесь копией, и переименование причины
// молча уводило бы часть событий в 'unclassified'
const NO_FORMATS_LABELS = {
    [NO_FORMATS_REASON.EMPTY]: 'no_formats',
    [NO_FORMATS_REASON.MULTIPLE]: 'multiple_videos',
    [NO_FORMATS_REASON.BLOCKED]: 'download_blocked',
    [NO_FORMATS_REASON.NO_CONVERTER]: 'no_converter',
    [NO_FORMATS_REASON.MALFORMED]: 'malformed_response',
    [NO_FORMATS_REASON.FAILED]: 'download_failed',
};

// Разбирает ошибку в {name, category, label, dimension1}. Порядок веток сохранён
// с прежней реализации showError().
function pickErrorEvent(e) {
    const notFound = (label) => ({ name: 'not_found', category: 'vidacha', label });

    if (!e) {
        return notFound('fatal');
    }
    if (e.timeout) {
        return notFound('timeout_exeeded');
    }
    // перегруз пула запросов, а не отказ бэкенда — отдельная метка, иначе всплеск
    // очереди неотличим от падения сервиса
    if (e.queue_timeout) {
        return notFound('queue_timeout');
    }
    // анти-abuse отказал в отправке — это не сбой бэкенда и не сеть
    if (e.signing_unavailable) {
        return notFound('signing_unavailable');
    }
    if (e.supported_resource === null) {
        return notFound('not_supported');
    }
    // ответ успешный, но карточке нечего показать. Без маркера такой отказ дошёл бы
    // до воронки через catch-all в конце, но обезличенно — маркер даёт точную причину
    if (e.no_formats) {
        return notFound(NO_FORMATS_LABELS[e.no_formats] || 'no_formats');
    }
    if (e.success === false || [100, 101, 102, 103].indexOf(e.code) > -1) {
        return notFound('link_not_found');
    }
    if (e.status >= 400 && e.status !== 401) {
        return notFound('link_not_found');
    }
    if (e.status === 401) {
        return {
            name: 'response-error',
            category: 'request-signing',
            // тело 401 не обязано быть объектом: сюда попадает и сырой axios-ответ
            label: (e.data && (e.data.error_code || e.data.error)) || '',
        };
    }
    if (e.blacklist) {
        return {
            name: 'blacklist',
            category: 'failure',
            label: 'blacklist',
            dimension1: e.srv || '',
        };
    }

    // форма ошибки, которую мы не разобрали. Молчать нельзя: невидимый
    // в воронке отказ занижает знаменатель success rate
    return notFound('unclassified');
}

export function getHost(link) {
    let aTmp = document.createElement('a');
    aTmp.href = link;
    return domainDictionary(aTmp.hostname);
}
